import {
  getStoredUserCountryCode,
  screenPop,
  searchAndScreenPop,
  setSoftphoneItemIcon,
  setSoftphonePanelVisibility,
} from "./acs.salesforce";
import parsePhoneNumber from "libphonenumber-js";
import { selfPhoneNumber, graphCallToGetNameOfResAcc } from "../../common/utils";
import { isDeferInboundScreenPop } from "./callCenterSettings";

const shouldRingFor = (incomingCall) => {
  const id = incomingCall?.callerInfo?.identifier;
  if (!id) return false;

  // Suppress ringing if the caller is a Teams user (direct Teams-to-Teams call).
  if (id && id?.kind === "microsoftTeamsUser") {
    if (
      incomingCall?.customContext?.voipHeaders?.some(
        (header) => header?.key === "unaricInitOutboundFromSf" && header?.value === "true",
      )
    ) {
      console.log("Allowing ring for direct Teams-to-Teams call");
      return true;
    }
    return false;
  }
  return true;
};

const handleScreenPopResult = ({
  callId,
  callerNumber,
  callerDisplayName,
  incomingCallMapRef,
  callMapRef,
  record,
  recordId,
  screenPopData,
  upsertCallMap,
  upsertIncomingCallMap,
}) => {
  let existingObjFromCallMap = {};
  let isAns = false;

  if (incomingCallMapRef.current.has(callId)) {
    existingObjFromCallMap = incomingCallMapRef.current.get(callId) || {};
  } else if (callMapRef.current.has(callId)) {
    //in case call is answered too quickly
    isAns = true;
    existingObjFromCallMap = callMapRef.current.get(callId) || {};
  }

  // Guard: if call ended before SF pop result arrived, don't resurrect it
  if (!existingObjFromCallMap || Object.keys(existingObjFromCallMap).length === 0) {
    return;
  }

  let { callerInfo } = existingObjFromCallMap;
  if (!callerInfo) {
    callerInfo = {
      callerNumber,
      callerName: callerDisplayName,
      matchedRecord: null,
      direction: "Inbound",
    };
  }

  // Update caller info with matched record (if record exists)
  if (record || recordId) {
    callerInfo.matchedRecord = {
      recordId: record?.whoId || record?.whatId || recordId || null,
      name: record?.whoName || record?.whatName || null,
      recordType: record?.whoType || record?.whatType || null,
    };
  }

  const payload = {
    callerInfo,
    whoId: record?.whoId || null,
    whoName: record?.whoName || null,
    whoType: record?.whoType || null,
    whatId: record?.whatId || null,
    whatName: record?.whatName || null,
    whatType: record?.whatType || null,
  };

  // Handle screen pop data if available
  if (screenPopData) {
    if (isAns || !isDeferInboundScreenPop()) {
      screenPop(screenPopData);
    } else {
      payload.screenPopData = screenPopData;
    }
  }

  // Upsert to appropriate call map
  if (isAns) {
    upsertCallMap(callId, payload);
  } else {
    upsertIncomingCallMap(callId, payload);
  }
};

export const getDateTime = (oldts) => {
  try {
    let now = new Date();
    if (oldts) {
      now = new Date(oldts);
    }

    if (isNaN(Date.parse(now))) {
      let dateTimeArr = now.split(" ");
      let date = dateTimeArr[0].split("-");
      let newDate = date[1] + "/" + date[2] + "/" + date[0] + " " + dateTimeArr[1];
      now = new Date(newDate);
    }

    let year = now.getFullYear();
    let month = now.getMonth() + 1;
    let day = now.getDate();
    let hour = now.getHours();
    let minute = now.getMinutes();
    let second = now.getSeconds();
    if (month.toString().length == 1) {
      month = "0" + month;
    }
    if (day.toString().length == 1) {
      day = "0" + day;
    }
    if (hour.toString().length == 1) {
      hour = "0" + hour;
    }
    if (minute.toString().length == 1) {
      minute = "0" + minute;
    }
    if (second.toString().length == 1) {
      second = "0" + second;
    }
    let dateTime = year + "-" + month + "-" + day + " " + hour + ":" + minute + ":" + second;
    return dateTime;
  } catch (error) {
    console.error("getDateTime Failed : ", error);
    return "YYYY-MM-DD HH:MM:SS";
  }
};

export function registerIncomingCallHandler({
  agent,
  setIncomingCall,
  incomingCallMapRef,
  upsertIncomingCallMap,
  removeFromIncomingCallMap,
  callMapRef,
  upsertCallMap,
  removeFromCallMap,
  setCallState,
  setCallerInfo,
  setIsMuted,
  setErrorMessage,
  setonAnotherDevice,
  lastCallMetaRef,
  callStartTimeRef,
  lastEntityIdRef,
  assignMetaRef,
  setDeletedAssignedRecId,
  setIsHold,
  setCallTransferState,
  setTransferorInfo,
}) {
  agent.on("incomingCall", async (args) => {
    try {
      let startTime = Date.now();
      // for setting the lastEntityIdRef to null beacause this value is not cleared in missed call case
      lastEntityIdRef.current = null;
      assignMetaRef.current = {
        recordId: null,
        recordName: null,
        objectType: null,
      };
      setDeletedAssignedRecId(null);
      console.log("incoming ar", args);
      //ignore 2nd call when there is already an active call in the system
      /*if(lastCallMetaRef?.current?.number) {
        return;
      }*/

      const incoming = args?.incomingCall;
      const ci = incoming?.callerInfo || {};

      console.log("Incoming call:", ci);

      const id = ci.identifier || {};
      const rawId = id?.rawId || "";
      const displayName = ci.displayName || "";

      if (!shouldRingFor(incoming)) {
        return;
      }
      /*if (typeof sforce !== "undefined" && sforce.opencti) {
        sforce.opencti.setSoftphonePanelHeight({
          heightPX: 340
        })
      }*/

      let callerNumber = ci?.identifier?.phoneNumber || null;
      const callerDisplayName = ci?.displayName || null; //Comes in case of transfer leg, agent 2 sees teams display name of Agent 1
      let callToPhNum = selfPhoneNumber(incoming?._teamsCall);
      let callerUpn = null;

      let parentCallIdInTransfer =
        incoming?.customContext?.voipHeaders?.find(
          (h) => h?.key === "unaricParentCallIdInTransfer" && h?.value,
        )?.value || "";

      let isBt =
        incoming?.customContext?.voipHeaders?.find((h) => h?.key === "unaricIsBt" && h?.value)
          ?.value || false;

      // Declare number early so it's available in event handlers
      let number = "";

      if (["TransferToParticipant", "CallQueue"].includes(incoming?.info?.callScenario)) {
        callToPhNum = "";
        let transferorInfo = {
          displayName: incoming?.transferorInfo?.displayName,
          callScenario: incoming?.info?.callScenario,
        };

        upsertIncomingCallMap(incoming.id, {
          acsCall: incoming,
          transferorInfo,
          // queue no. is not there so thats why this field is empty
          callToPhone: callToPhNum,
        });

        if (
          !transferorInfo?.displayName &&
          (ci?.identifier?.kind === "phoneNumber" ||
            ci?.identifier?.kind === "microsoftTeamsUser") &&
          incoming?._callCommon?._transferorIdentity.kind === "microsoftTeamsApp" &&
          incoming?._callCommon?._transferorIdentity.teamsAppId &&
          ((transferorInfo?.callScenario === "TransferToParticipant" &&
            incoming?._teamsCall?.tsCall?.incomingCallType === "transfer") ||
            (transferorInfo?.callScenario === "CallQueue" &&
              incoming?._teamsCall?.tsCall?.incomingCallType === "forward"))
        ) {
          graphCallToGetNameOfResAcc(incoming?._callCommon?._transferorIdentity.teamsAppId)
            .then((resAccNameOfAutoAttendant) => {
              if (resAccNameOfAutoAttendant) {
                transferorInfo.displayName = resAccNameOfAutoAttendant;
                transferorInfo.callScenario = "CallQueue";
                let isAns = false;
                if (incomingCallMapRef.current.has(incoming.id)) {
                  // still ringing
                } else if (callMapRef.current.has(incoming.id)) {
                  //in case call is answered too quickly
                  isAns = true;
                } else {
                  return; // call already ended, don't resurrect
                }
                if (isAns) {
                  upsertCallMap(incoming.id, { transferorInfo });
                } else {
                  upsertIncomingCallMap(incoming.id, { transferorInfo });
                }
              }
            })
            .catch((err) => {
              console.error("graphCallToGetNameOfResAcc failed:", err);
            });
        }
      }

      callerUpn =
        incoming?.customContext?.voipHeaders?.find((h) => h?.key === "unaricCallerUpn" && h?.value)
          ?.value || ""; //as we need it also for bt done after ct which is pstn call

      upsertIncomingCallMap(incoming.id, {
        acsCall: incoming,
        direction: "Inbound",
        callState: "Ringing",
        startedAt: startTime,
        startedAtUtc: new Date(startTime).toISOString().split(".")[0] + "Z",
        startedAtDateTimeStr: getDateTime(startTime),
        callerInfo: {
          callerNumber,
          callerName: callerDisplayName,
          matchedRecord: null,
          direction: "Inbound",
        },
        callFromPhone: callerNumber, //Inbound = Customer phonenum - used for search, assignment
        callerGraphUserId: id?.kind === "microsoftTeamsUser" ? id?.microsoftTeamsUserId : null,
        callFromUpn: !parentCallIdInTransfer || !isBt ? callerUpn || null : null, //Inbound = Customer UPN = Other agent's UPN = internal call / transfer leg
        transferFromUpn: parentCallIdInTransfer && isBt ? callerUpn || null : null, //not for internal call, only for original BT and pstn leg of complete transferred call (BT), and not teams to teams leg
        callToPhone: callToPhNum, //Inbound = Agent's number
        callHandlerId: selfPhoneNumber(incoming?._teamsCall),
      });

      // Pop the softphone window
      setSoftphonePanelVisibility(true);

      // transferorInfoChanged is change from acs thats why tracking its event changes
      incoming.on("transferorInfoChanged", () => {
        console.log("transferorInfoChanged");
        if (["TransferToParticipant", "CallQueue"].includes(incoming?.info?.callScenario)) {
          //setTransferorInfo({ displayName: name, callScenario: incoming?.info?.callScenario });
          const updatedTransferorInfo = {
            displayName: incoming?.transferorInfo?.displayName,
            callScenario: incoming?.info?.callScenario,
          };
          let isAns = false;
          if (incomingCallMapRef.current.has(incoming.id)) {
            // still ringing
          } else if (callMapRef.current.has(incoming.id)) {
            //in case call is answered too quickly
            isAns = true;
          } else {
            return; // call already ended, don't resurrect
          }
          callToPhNum = "";
          if (isAns) {
            upsertCallMap(incoming.id, {
              transferorInfo: updatedTransferorInfo,
              callToPhone: callToPhNum,
            });
          } else {
            upsertIncomingCallMap(incoming.id, {
              acsCall: incoming,
              transferorInfo: updatedTransferorInfo,
              // queue no. is not there so thats why this field is empty
              callToPhone: callToPhNum,
            });
          }
        }
      });
      // Track call state
      if (incoming?.on) {
        incoming.on("stateChanged", () => {
          console.log("Incoming call state:", incoming.state);
          //setCallState(incoming.state);
          upsertIncomingCallMap(incoming.id, { callState: incoming.state });
        });
      }

      // Handle caller hangup / answered elsewhere
      incoming.on("callEnded", (ev) => {
        console.log("Incoming call ended before accept:", ev);

        /**
         * a). If rejected from Acs or Teams -> code=603, subCode=0, message="Call ended successfully as it was declined from callee."
         * b). else if initial caller terminated call in ring state -> code=487, subCode=540487, message="Call ended successfully as caller canceled the call., \n..."
         * c). else if answered on other device -> code=487, subCode=10003, message="Call was cancelled for this user endpoint as it was was accepted elsewhere, by another endpoint., \nT"
         * for only a,b -> create missed call task in sf
         */
        console.log(
          `Call ended, reason: code=${ev?.callEndReason?.code}, subCode=${ev?.callEndReason?.subCode}`,
        );

        if (ev?.callEndReason?.code === 487 && ev?.callEndReason?.subCode === 10003) {
          // do nothing as its answered on other device
          removeFromIncomingCallMap(incoming.id);
        } else {
          const endTs = Date.now();
          const endedAtUtc = new Date(endTs).toISOString().split(".")[0] + "Z";

          /*const callResult = `${ev.callEndReason.code || ""}${
            ev.callEndReason.subCode
              ? "/" + ev.callEndReason.subCode
              : ""
            }`;*/
          const callResult = "";
          const endReason = ev.callEndReason.message.split(".")[0]; //(callResult ? callResult + " - " : "") +
          const durationSec = 0;

          const notes = "";

          //const meta = lastCallMetaRef.current || {};
          const meta = incomingCallMapRef.current.get(incoming.id) || {};
          console.log("meta", meta);

          const startedAtTs = meta?.startedAt;
          const ringDurationSec = startedAtTs
            ? Math.max(0, Math.round((endTs - startedAtTs) / 1000))
            : 0;

          const holdDurationSec = 0;
          setSoftphoneItemIcon("call");
          /*const queueName = meta?.transferorInfo?.displayName;
          if(!queueName) {
            logSalesforceActivity({
              direction: meta.direction || "Inbound",
              number: meta.callerInfo?.callerNumber || number || "",
              name: meta.callerInfo?.matchedRecord?.name || meta.callerInfo?.callerName || "",
              matchedRecordId: meta?.callerInfo?.matchedRecord?.recordId,
              whoId: meta?.whoId, 
              whoName: meta?.whoName, 
              whoType: meta?.whoType,
              whatId: meta?.whatId, 
              whatName: meta?.whatName, 
              whatType: meta?.whatType,
              durationSec,
              endReason,
              callResultGroup: meta?.callResultGroup || "",
              callResult: meta?.callResult || "", 
              callId: incoming.id,
              notes,
              subject,
              //lastEntityIdRef,
              //assignMetaRef,
              ringDurationSec,
              holdDurationSec,
              startedAt: meta.startedAt,
              startedAtDateTimeStr: meta.startedAtDateTimeStr,
              startedAtUtc : meta.startedAtUtc,
              endedAtUtc,
              queueName,
              callFromPhone: meta?.callFromPhone,
              callToPhone: meta?.callToPhone,
              callToUPN: null,
              callHandlerId: meta?.callHandlerId,
              callerGraphUserId: meta?.callerGraphUserId,
              setErrorMessage,
              isRetry: false,
            });
          }*/ // due to multiple tab issue, for now commenting code to log missed calls from softphone
        }

        removeFromIncomingCallMap(incoming.id);
        lastEntityIdRef.current = null;
        assignMetaRef.current = {
          recordId: null,
          recordName: null,
          objectType: null,
        };
        setDeletedAssignedRecId(null);
        // if (
        //   ev?.callEndReason?.code === 487 &&
        //   ev?.callEndReason?.subCode === 10003
        // ) {
        //   // Answered on another Teams device
        //   setonAnotherDevice(true);
        // } else {
        //   setErrorMessage("");
        // }
      });

      /** ---------------- Salesforce CTI ---------------- */
      if (typeof sforce === "undefined") return;

      // Salesforce CTI integration
      setSoftphoneItemIcon("incoming_call");

      let searchParams;
      let popParams = { callId: incoming.id, callType: "Inbound" };

      if (rawId === "4:anonymous") {
        popParams = { ...popParams, name: displayName, source: "pstn" };
        searchParams = incoming.id; //searchParams is mandatory for SF pop up, so using incoming id as search param for anonymous call which will not match any record but will pop up the softphone with incoming id info
      } else if (id?.phoneNumber) {
        const countryCode = getStoredUserCountryCode();
        let parsedPhoneNumber;
        number = id.phoneNumber;

        try {
          parsedPhoneNumber = countryCode
            ? parsePhoneNumber(number, countryCode)
            : parsePhoneNumber(number);
        } catch (error) {
          parsedPhoneNumber = null;
        }

        if (parsedPhoneNumber?.isValid()) {
          searchParams = parsedPhoneNumber.number + " or " + parsedPhoneNumber.nationalNumber;
        } else {
          searchParams = String(number).replace(/[^\d+]/g, "");
        }
        popParams = { ...popParams, name: displayName, number, source: "pstn" };

        /*lastCallMetaRef.current = {
          direction: "Inbound",
          number: e164,
          name: displayName,
        };*/
        upsertIncomingCallMap(incoming.id, {
          direction: "Inbound",
          number: number,
          name: displayName,
          searchParams,
        });
      } else {
        const name = displayName || rawId;
        searchParams = name;
        popParams = { ...popParams, name, id: rawId, source: "teams" };

        upsertIncomingCallMap(incoming.id, {
          direction: "Inbound",
          number: callerNumber || null,
          name,
          searchParams,
        });
      }
      // SF pop up
      searchAndScreenPop({
        searchParams,
        callType: sforce.opencti.CALL_TYPE.INBOUND,
        popParams,
        shouldPop: false,
        onNoMatch: ({ popData }) => {
          handleScreenPopResult({
            callId: incoming.id,
            callerNumber,
            callerDisplayName,
            incomingCallMapRef,
            callMapRef,
            record: null,
            recordId: null,
            screenPopData: popData,
            upsertCallMap,
            upsertIncomingCallMap,
          });
        },
        onSuccess: ({ recordId, record, popData }) => {
          handleScreenPopResult({
            callId: incoming.id,
            callerNumber,
            callerDisplayName,
            incomingCallMapRef,
            callMapRef,
            record,
            recordId,
            screenPopData: popData,
            upsertCallMap,
            upsertIncomingCallMap,
          });
        },
      });

      // Track active SF record
      /*try {
        sforce.opencti.getPageInfo?.({
          callback: (res) => {
            const id = res?.result?.objectId || res?.result?.recordId;
            if (id) lastEntityIdRef.current = id;
			else lastEntityIdRef.current = null;
          },
        });

        sforce.opencti.onNavigationChange?.({
          listener: (evt) => {
            const id = evt?.objectId || evt?.recordId;
            if (id) lastEntityIdRef.current = id;
			else lastEntityIdRef.current = null;
          },
        });
      } catch (e) {
        console.warn("Failed to wire Salesforce page listeners:", e);
      }*/
    } catch (error) {
      console.error("Incoming call handler error:", error);
    }
  });
}
